import classNames from "classnames";
import {
  ButtonBack,
  ButtonNext,
  CarouselProvider,
  Slide,
  Slider,
} from "pure-react-carousel";
import "pure-react-carousel/dist/react-carousel.es.css";
import React, { useContext, useEffect, useMemo, useState } from "react";
import { PagesContext } from "../contexts/PagesContext";
import { RTF } from "../elements/RTF";
import { useEntryAnimation } from "../hooks/Visibility";
import { useMediaQuery } from "../hooks/mediaQuery";
import { ArrowIcon } from "../icons/ArrowIcon";
import { HalfWidthClassName } from "../styles";
import { buildThumbnailLinksFromPages } from "./BlockCollection";
import { CollectionProps } from "./Collection";

export const PageCarousel: React.FC<CollectionProps> = ({
  title,
  message,
  contents,
  className,
  pageFilterCount,
  pageTypeFilter,
}) => {
  const { cmsPages } = useContext(PagesContext);
  const [slideCount, setSlideCount] = useState(1);
  const isNotMobile = useMediaQuery("(min-width: 768px)");
  useEffect(() => setSlideCount(isNotMobile ? 2 : 1), [isNotMobile]);

  const [sectionRef, animationMode] = useEntryAnimation(0.01);
  const slides = useMemo(
    () =>
      pageFilterCount && pageTypeFilter
        ? buildThumbnailLinksFromPages(
            cmsPages,
            pageTypeFilter,
            pageFilterCount,
          )
        : contents.map(({ content }) => content),
    [cmsPages, pageFilterCount, pageTypeFilter, contents],
  );

  return (
    <section
      ref={sectionRef}
      data-component="Page Carousel"
      className={classNames(
        "py-section relative py-48px lg:py-150px",
        className,
      )}
    >
      <div className="page-grid max-width relative">
        <div
          className={classNames(
            HalfWidthClassName,
            animationMode,
            "animate-appear",
          )}
        >
          {title && <h2 className={classNames("h3 text-hyacinth")}>{title}</h2>}
          {message && <RTF content={message} fixedParagraphClassName="p1" />}
        </div>
        <CarouselProvider
          className={classNames(
            "col-span-full col-start-1",
            "pt-48px",
            "lg:pt-100px",
            "page-grid",
          )}
          visibleSlides={slideCount}
          naturalSlideWidth={1}
          naturalSlideHeight={1}
          totalSlides={slides.length}
          isIntrinsicHeight={true}
        >
          <ButtonBack
            className={classNames(
              "col-span-1 col-start-2 row-start-2 justify-self-start",
              "lg:col-span-2 lg:col-start-1 lg:row-start-1 lg:justify-self-center",
            )}
          >
            <ArrowIcon className="text-lazer" />
          </ButtonBack>
          <Slider
            className={classNames(
              "row-start-1",
              "col-span-4 col-start-2",
              "md:col-span-8 md:col-start-2",
              "lg:col-span-12 lg:col-start-3",
              "pb-28 lg:pb-0 ",
            )}
          >
            {slides.map((s, i) => (
              <Slide key={i} index={i}>
                <div
                  className={classNames(
                    "transform-gpu",
                    i % 2 === 1 && "md:mt-40px lg:mt-100px",
                    "mx-auto md:w-tablet-4-cols lg:w-desktop-6-cols",
                  )}
                >
                  {s}
                </div>
              </Slide>
            ))}
          </Slider>
          <ButtonNext
            className={classNames(
              "col-span-1 col-start-5 row-start-2 justify-self-end",
              "md:col-span-1 md:col-start-9",
              "lg:col-span-2 lg:col-start-15 lg:row-start-1 lg:justify-self-center",
            )}
          >
            <ArrowIcon className="rotate-180 transform-gpu text-lazer" />
          </ButtonNext>
        </CarouselProvider>
      </div>{" "}
    </section>
  );
};
